/**
 * AIChatMessageBubble - Shared message bubble component for AI chat panels
 *
 * Used by both AIChatPanel (article-specific) and GlobalAIChatPanel (RAG search).
 */

import { Sparkles, Trash2, Copy, BookOpen, MessageSquare, Search, BookmarkCheck, MessageSquareDot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { stripReedleDirectives } from '../../utils/stripMarkdown';
import { stripCitations } from '../../hooks/useCitationParser';
import { ICON_SIZE } from '../../design-system';
import useAuth from '../../hooks/useAuth';
import { openModal } from '../../store';
import UserAvatar from '../UserAvatar';

// AI Chat capsule labels - outside component to avoid re-creation
const CAPSULE_LABELS = {
  dictionary: 'lookUp',
  grammar: 'grammar',
  makecard: 'makeCards',
};

/**
 * Single message bubble with avatar and actions
 *
 * @param {Object} props
 * @param {Object} props.message - The message object { role: 'user'|'assistant', content: string }
 * @param {number} props.index - Message index in the conversation
 * @param {function} props.onDelete - Callback when delete button is clicked
 * @param {React.ReactNode} props.children - Custom content to render instead of default
 * @param {React.ReactNode} props.actions - Additional action buttons (prepended to delete button)
 * @param {React.ReactNode} props.footer - Footer content (e.g., sources)
 * @param {function} props.onAddToDeck - Callback for "Add to Deck" button (article chat only)
 * @param {function} props.onAddAsComment - Callback for "Add as Comment" button (article chat only)
 * @param {boolean} props.isSavedToDeck - Whether this message is saved to deck
 * @param {boolean} props.isSavedAsComment - Whether this message is saved as comment
 * @param {function} props.onUnsaveFromDeck - Callback to unsave from deck
 * @param {function} props.onUnsaveComment - Callback to unsave comment
 * @param {boolean} props.isStreaming - Whether this message is currently streaming (hides action buttons)
 */
const AIChatMessageBubble = ({
  message,
  index,
  onDelete,
  children,
  actions,
  footer,
  onAddToDeck,
  onAddAsComment,
  isSavedToDeck = false,
  isSavedAsComment = false,
  onUnsaveFromDeck,
  onUnsaveComment,
  isStreaming = false,
}) => {
  const { t } = useTranslation('reader');
  const { user } = useAuth();

  const handleDelete = () => {
    openModal('confirmDelete', {
      header: t('aiChat.deleteMessage'),
      message: t('aiChat.deleteMessageConfirm'),
      confirmText: t('modals:confirm.deleteButton'),
      showDontAskAgain: false,
      onConfirm: () => onDelete?.(index),
    });
  };

  const handleCopy = async () => {
    try {
      // Strip both reedle directives and citation markers [n] for clean clipboard text
      const cleanText = stripCitations(stripReedleDirectives(message.content));
      await navigator.clipboard.writeText(cleanText);
      toast(t('toast.copiedToClipboard'));
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy');
    }
  };

  const isAssistant = message.role === 'assistant';

  const handleDeckAction = () => {
    if (isSavedToDeck) {
      onUnsaveFromDeck?.(index);
    } else {
      onAddToDeck?.(index);
    }
  };

  const handleCommentAction = () => {
    if (isSavedAsComment) {
      onUnsaveComment?.(index);
    } else {
      onAddAsComment?.(index);
    }
  };

  return (
    <div className={`ai-chat-message ${message.role}`}>
      <div className="ai-chat-message-avatar">
        {isAssistant ? <Sparkles size={ICON_SIZE.sm} /> : (
          <UserAvatar
            userId={user?.id}
            username={user?.username}
            avatarUrl={user?.avatar_url}
            size={24}
            className="ai-chat-user-avatar"
          />
        )}
      </div>
      <div className="ai-chat-message-content">
        {children}
        {footer}

        {/* Inline action buttons - hidden during streaming, visible after animation completes */}
        {!isStreaming && (
        <div className="ai-chat-inline-actions">
          {/* Left group: Deck, Comment, Custom actions (assistant only) */}
          <div className="ai-chat-left-actions">
            {isAssistant && onAddToDeck && (
              <button
                className={`ai-chat-action-btn ${isSavedToDeck ? 'saved' : ''}`}
                onClick={handleDeckAction}
                title={isSavedToDeck ? t('aiChat.removeFromDeck') : t('aiChat.saveToDeck')}
              >
                {isSavedToDeck ? <BookmarkCheck size={ICON_SIZE.xs} /> : <BookOpen size={ICON_SIZE.xs} />}
                <span>{isSavedToDeck ? t('aiChat.savedToDeck') : t('aiChat.saveToDeck')}</span>
              </button>
            )}
            {isAssistant && onAddAsComment && (
              <button
                className={`ai-chat-action-btn ${isSavedAsComment ? 'saved' : ''}`}
                onClick={handleCommentAction}
                title={isSavedAsComment ? t('aiChat.removeComment') : t('aiChat.saveAsComment')}
              >
                {isSavedAsComment ? <MessageSquareDot size={ICON_SIZE.xs} /> : <MessageSquare size={ICON_SIZE.xs} />}
                <span>{isSavedAsComment ? t('aiChat.commented') : t('aiChat.saveAsComment')}</span>
              </button>
            )}
            {actions}
          </div>

          {/* Right group: Copy, Delete (all messages) */}
          <div className="ai-chat-right-actions">
            <button
              className="ai-chat-copy-btn"
              onClick={handleCopy}
              title={t('aiChat.copyToClipboard')}
            >
              <Copy size={ICON_SIZE.xs} />
            </button>
            {onDelete && (
              <button
                className="ai-chat-delete-btn"
                onClick={handleDelete}
                title={t('aiChat.deleteMessage')}
              >
                <Trash2 size={ICON_SIZE.xs} />
              </button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

/**
 * Special message capsule for dictionary/grammar/makecard lookups
 * Used in article chat when user triggers special actions on highlighted text
 *
 * @param {Object} props
 * @param {string} props.type - 'dictionary' | 'grammar' | 'makecard'
 * @param {string} props.text - The quoted text
 * @param {React.ReactNode} props.icon - Icon component to display
 */
export const AIChatCapsule = ({ type, text, icon }) => {
  const { t } = useTranslation('reader');
  const label = t(`aiChat.capsuleLabels.${CAPSULE_LABELS[type]}`);

  const maxLength = type === 'makecard' ? 40 : type === 'grammar' ? 50 : 100;
  const displayText = text.length > maxLength ? text.slice(0, maxLength) + '...' : text;

  return (
    <div className={`ai-dict-capsule ${type !== 'dictionary' ? `ai-${type}-capsule` : ''}`}>
      {icon}
      <span>{t('aiChat.capsuleLabelFormat', { label, text: displayText })}</span>
    </div>
  );
};

/**
 * Typing indicator (three dots animation)
 */
export const AIChatTypingIndicator = () => (
  <div className="ai-chat-message assistant">
    <div className="ai-chat-message-avatar">
      <Sparkles size={ICON_SIZE.sm} />
    </div>
    <div className="ai-chat-message-content typing">
      <span className="typing-dot"></span>
      <span className="typing-dot"></span>
      <span className="typing-dot"></span>
    </div>
  </div>
);

/**
 * Searching indicator (for RAG panel)
 */
export const AIChatSearchingIndicator = ({ text = 'Searching articles...' }) => (
  <div className="ai-chat-message assistant">
    <div className="ai-chat-message-avatar">
      <Sparkles size={ICON_SIZE.sm} />
    </div>
    <div className="ai-chat-message-content">
      <div className="rag-searching">
        <Search size={ICON_SIZE.xs} className="searching-icon" />
        <span>{text}</span>
      </div>
    </div>
  </div>
);

/**
 * Error display
 */
export const AIChatError = ({ error }) => (
  error ? <div className="ai-chat-error">{error}</div> : null
);

export default AIChatMessageBubble;
